import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { authApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const LOGO = "";
import { BullMark } from "@/components/dochub/BullMark";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { setSessionFromToken } = useAuth();
  const nav = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setError("Missing token"); return; }
    authApi.getInvite(token)
      .then((i) => { setInvite(i); setFullName(i.full_name || ""); })
      .catch((e) => setError(e?.response?.data?.detail || "Invalid invite"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await authApi.accept(token, password, fullName);
      setSessionFromToken(res.token, res.user);
      toast.success("Welcome to DocHub");
      nav(res.user?.role === "branch_user" ? "/scan" : "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to accept invite");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas p-6">
      <div className="max-w-md w-full bg-white border border-canvas-border rounded-2xl p-10 shadow-soft">
        <div className="flex items-center gap-3 mb-8">
          <BullMark className="w-12 h-12" />
          <div>
            <div className="font-display text-2xl font-bold text-espresso">DocHub</div>
            <div className="text-xs uppercase tracking-widest text-espresso-ink/50">Accept invitation</div>
          </div>
        </div>
        {error && <div className="p-4 bg-status-duplicate/10 border border-status-duplicate/20 text-status-duplicate rounded-lg text-sm">{error} · <Link to="/login" className="underline">Back to login</Link></div>}
        {invite && (
          <form onSubmit={submit} className="space-y-4" data-testid="accept-invite-form">
            <div>
              <div className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Email</div>
              <div className="mt-1 text-espresso font-medium">{invite.email}</div>
              <div className="mt-2 inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest bg-gold-soft text-gold rounded">
                {invite.role?.replace("_", " ")}
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Full name</label>
              <input
                data-testid="accept-name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                className="w-full mt-1.5 px-4 py-3 bg-white border border-canvas-border rounded-lg brand-focus"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Choose a password</label>
              <input
                data-testid="accept-password"
                type="password"
                minLength={8}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full mt-1.5 px-4 py-3 bg-white border border-canvas-border rounded-lg brand-focus"
              />
            </div>
            <button data-testid="accept-submit" disabled={loading} className="w-full py-3 bg-espresso text-canvas rounded-lg font-semibold hover:bg-espresso-light transition-colors flex items-center justify-center gap-2">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Accept &amp; sign in
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
